import { useCallback, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, LexicalEditor } from "lexical";
import { $patchStyleText } from '@lexical/selection';
import ColorPicker from "../../components/ColorPicker";
import { FontFamily } from "./actions/FontFamily";
import { faBug, faFillDrip, faPen, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import Menu from "../../components/Menu";
import { FontSize } from "../FontSize";
import { BlockType } from "../BlockType";
import { Color } from "../../../../lib/Graphics/Color";
import { Undo, Redo } from './actions/History';
import { Media } from "./actions/Media";
import { ExternalMedia } from "./actions/ExternalMedia";
import { CallbackFunction } from "../../../../lib/Promises";
import { Layout } from "./actions/Layout";
import { Table } from "./actions/Table";
import Alignment from "./actions/Alignment";
import List from "./actions/List";
import TextFormat from "./actions/TextFormat";
import Link from "./actions/Link";
import "./Toolbar.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuItemAdapter } from "./MenuItemAdapter";
import CustomAction from "./actions/CustomAction";
import ToolbarButton from "../../components/ToolbarButton";

export interface IToolbarActionDefinition
{
    type: string
    text?: string
    icon?: IconDefinition
    hint?: string
    group?: string
    onSelected?: (editor: LexicalEditor) => void
    loadChildren?: CallbackFunction<IToolbarActionDefinition[]>
}

export type ToolbarProperties =
{
    actions?: IToolbarActionDefinition[],
    debugMode?: boolean
}

export default function Toolbar({ actions, debugMode }: ToolbarProperties)
{
    const [editor] = useLexicalComposerContext();
    const [fontColor] = useState<string>('#000');
    const [backgroundColor] = useState<string>('#fff');
    const applyStyleText = useCallback((styles: Record<string, string>) =>
    {
        editor.update(() =>
        {
            const selection = $getSelection();
            if ($isRangeSelection(selection))
            {
                $patchStyleText(selection, styles);
            }
        });
    },
    [editor]);
    const onFontColorSelect = useCallback((color: Color) => { applyStyleText({ color: color.hex }); }, [applyStyleText] );
    const onBackgroundColorSelect = useCallback((value: Color) => { applyStyleText({ 'background-color': value.hex }); }, [applyStyleText] );
    const handleMenuItemSelected = useCallback((item: MenuItemAdapter) =>
    {
        item.action.onSelected?.(editor);
    },
    [editor]);
    const handleDebug = useCallback(() =>
    {
        const editorState = editor.getEditorState();
        // eslint-disable-next-line no-debugger
        debugger;
    },
    []);
    const actionsEnd = actions?.filter(a => a.group === 'end');
    const actionsOther = actions?.filter(a => a.group !== 'end');
    const element =
    <>
        <div className="toolbar">
            <Undo />
            <Redo />
            <div className="divider" />
            <Media />
            <ExternalMedia />
            <Link />
            {actionsOther?.map((action, index) => <CustomAction key={index} action={action} editor={editor} onClick={handleMenuItemSelected} />)}
            <div className="divider" />
            <BlockType />
            <FontFamily />
            <FontSize />
            <div className="divider" />
            <TextFormat />
            <div className="divider" />
            <Alignment />
            <div className="divider" />
            <ColorPicker ariaLabel="Formatting text color" color={fontColor} onSelect={onFontColorSelect} onChange={onFontColorSelect} icon={faPen} title="text color" />
            <ColorPicker ariaLabel="Formatting background color" color={backgroundColor} onSelect={onBackgroundColorSelect} onChange={onBackgroundColorSelect} icon={faFillDrip} title="background color" />
            <List />
            <div className="divider" />
            <Layout />
            <Table />
            {debugMode &&
            <>
                <div className="divider" />
                <ToolbarButton icon={faBug} title="Debug" onClick={handleDebug} />
            </>}
            <div className="divider" />
            {actionsEnd?.map((action, index) => <CustomAction key={index} action={action} editor={editor} onClick={handleMenuItemSelected} />)}
        </div>
    </>;
    return element;
}
